import { cn } from '../cn';

import type { ButtonHTMLAttributes } from 'react';

/**
 * `outline`: a white pill with a default border; selected, it turns info (tint, border and
 * text) and semibold. Used for toggles and pill tabs (design `.st-chip`, the UTM key pills).
 * `quiet`: a subdued pill with a subdued border that darkens on hover; selected, it turns
 * info at the same weight (design `.ov-chip`, the page numbers of a pager). One size.
 * `outline` size `xs` is the filter chip of the product pages (design `.ppm-chip`,
 * `.pd-metricpick`): 30 px tall, 12.5 px, regular weight until selected, and no colour fade.
 */
export type ChipVariant = 'outline' | 'quiet';
export type ChipSize = 'xs' | 'sm' | 'md';

const OUTLINE_SIZE: Record<ChipSize, string> = {
  xs: 'h-[30px] gap-1.5 px-[11px] text-[12.5px]',
  sm: 'h-8 gap-1.5 px-3.5 text-[13px]',
  md: 'h-10 gap-2 px-4.5 text-sm',
};

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ChipVariant | undefined;
  /** `outline` only: `xs` 30 px tall, 12.5 px; `sm` 32 px tall, 13 px; `md` 40 px tall, 14 px. */
  size?: ChipSize | undefined;
  /** The selected look. Set the ARIA state yourself (`aria-pressed`, `aria-selected`, ...). */
  selected?: boolean | undefined;
  /** `outline` only: tint the background on hover while not selected, with a 150 ms fade. */
  hoverTint?: boolean | undefined;
}

export function Chip({
  variant = 'outline',
  size = 'sm',
  selected = false,
  hoverTint = false,
  className,
  children,
  ...rest
}: ChipProps) {
  const on =
    'border-surface-static-info-accented bg-surface-static-info-default text-surface-static-info-accented';
  const look =
    variant === 'quiet'
      ? cn(
          'min-w-[34px] justify-center gap-1.5 px-3 py-1.5 text-[13px] font-medium transition-all duration-150',
          selected
            ? on
            : 'border-border-subdued bg-surface-static-default text-content-subdued hover:border-border-default hover:text-content-default',
        )
      : cn(
          OUTLINE_SIZE[size],
          selected
            ? cn(on, 'font-semibold')
            : cn(
                'border-border-default bg-surface-static-default text-content-default',
                size === 'xs' ? 'font-normal' : 'font-medium',
                hoverTint && 'hover:bg-surface-static-neutral-default',
              ),
          hoverTint && size !== 'xs' && 'transition-colors duration-150',
        );
  return (
    <button
      type="button"
      className={cn(
        'inline-flex cursor-pointer items-center rounded-full border whitespace-nowrap',
        look,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
